import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AppLogo } from "@/components/app/Primitives";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import TID from "@/lib/testIds";
import * as Icons from "lucide-react";
import { Download, Printer, RefreshCw, FileText } from "lucide-react";
import { toast } from "sonner";

// Map kebab-case icon names to lucide-react component names
function iconFor(name) {
  if (!name) return Icons.FileText;
  const pascal = name.split("-").map((s) => s[0].toUpperCase() + s.slice(1)).join("");
  return Icons[pascal] || Icons.FileText;
}

const TAG_STYLE = {
  "LIVE": "bg-emerald-100 text-emerald-800 border-emerald-300",
  "DEMO · SIMULATED": "bg-amber-100 text-amber-800 border-amber-300",
  "LIVE + DEMO": "bg-sky-100 text-sky-800 border-sky-300",
};

function Tag({ tag }) {
  if (!tag) return null;
  return <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${TAG_STYLE[tag] || "bg-slate-100 text-slate-700 border-slate-300"}`} data-testid="guide-tag">{tag}</span>;
}

function RaciTable({ section }) {
  return (
    <div className="mt-6">
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 mb-5">{section.sections[0]?.p}</div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm border">
          <thead className="bg-slate-100 text-xs uppercase tracking-widest text-slate-600"><tr><th className="text-left px-3 py-2 w-1/3">Step</th><th className="text-left px-3 py-2">Roles &amp; RACI</th></tr></thead>
          <tbody>
            {section.sections.slice(1).map((b) => (
              <tr key={b.h} className="border-t hover:bg-slate-50">
                <td className="px-3 py-2 font-semibold text-slate-800 align-top">{b.h}</td>
                <td className="px-3 py-2 text-slate-700 align-top">{b.p.split("·").map((part) => <span key={part.trim()} className="inline-block mr-3 mb-1 whitespace-nowrap rounded bg-slate-100 px-2 py-0.5 text-xs">{part.trim()}</span>)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function LevelCards({ section }) {
  const parse = (p) => p.split(" · ").map((kv) => { const i = kv.indexOf(":"); return i > 0 ? [kv.slice(0, i).trim(), kv.slice(i + 1).trim()] : ["", kv.trim()]; });
  const tone = { Can: "text-emerald-700", Cannot: "text-rose-700", Login: "font-mono text-slate-800", Logins: "font-mono text-slate-800", Home: "font-mono text-[#0052FF]" };
  return (
    <div className="mt-6 space-y-4">
      <div className="rounded-lg border border-sky-200 bg-sky-50 p-3 text-xs text-sky-900">{section.sections[0]?.p}</div>
      <div className="grid md:grid-cols-2 gap-4">
        {section.sections.slice(1).map((b) => (
          <div key={b.h} className="rounded-xl border border-slate-200 p-4 bg-slate-50/60" data-testid={`guide-level-${b.h.split("·")[0].trim().replace(/\s+/g, "-").toLowerCase()}`}>
            <div className="font-heading font-bold text-slate-900">{b.h}</div>
            <dl className="mt-2 space-y-1.5 text-xs">
              {parse(b.p).map(([k, v]) => <div key={k + v.slice(0, 12)} className="grid grid-cols-[64px_1fr] gap-2"><dt className="uppercase tracking-wider text-slate-400 font-semibold">{k}</dt><dd className={tone[k] || "text-slate-700"}>{v}</dd></div>)}
            </dl>
          </div>
        ))}
      </div>
    </div>
  );
}

function GuideBody({ section }) {
  if (section.id === "raci") return <RaciTable section={section} />;
  if (section.id === "user_levels") return <LevelCards section={section} />;
  return (
    <div className="mt-8 space-y-6">
      {section.sections.map((b) => (
        <div key={b.h} className="border-l-2 border-[#0052FF]/30 pl-5">
          <div className="flex flex-wrap items-center gap-2"><span className="font-heading text-lg font-bold text-slate-900">{b.h}</span><Tag tag={b.tag} /></div>
          <p className="text-slate-600 mt-2 leading-relaxed whitespace-pre-line">{b.p}</p>
        </div>
      ))}
    </div>
  );
}

export default function Guide() {
  const [doc, setDoc] = useState(null);
  const [active, setActive] = useState("overview");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/guide/full");
      setDoc(data);
      if (data.sections?.length && !data.sections.find((s) => s.id === active)) {
        setActive(data.sections[0].id);
      }
    } catch (err) {
      console.debug("guide load failed:", err?.message);
      toast.error("Could not load the guide");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // `load` intentionally has no closed-over state — safe to run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sections = useMemo(() => doc?.sections || [], [doc]);
  const current = useMemo(() => sections.find((s) => s.id === active) || sections[0], [sections, active]);

  const handleDownloadPdf = () => {
    // Add print class so only the guide content is included, then trigger the
    // browser's print dialog. Users can save as PDF.
    document.body.classList.add("printing-guide");
    window.print();
    setTimeout(() => document.body.classList.remove("printing-guide"), 1000);
  };

  return (
    <div className="min-h-screen bg-slate-50 overflow-x-hidden">
      {/* Screen-only nav */}
      <div className="bg-white border-b border-slate-200 no-print">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <Link to="/" className="flex-shrink-0"><AppLogo /></Link>
          <div className="flex items-center gap-2 min-w-0">
            <Button
              variant="outline"
              size="sm"
              onClick={load}
              data-testid="guide-refresh"
              className="hidden sm:inline-flex"
            >
              <RefreshCw size={14} className="mr-1.5" /> Refresh
            </Button>
            <Button
              data-testid="guide-download-pdf"
              onClick={handleDownloadPdf}
              className="bg-[#0052FF] hover:bg-[#0043D9]"
              size="sm"
            >
              <Download size={14} className="mr-1.5" /> <span className="hidden xs:inline">Download </span>PDF
            </Button>
            <Link to="/login" className="text-sm text-slate-600 hover:text-slate-900 px-2 hidden sm:inline">Sign in</Link>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10 grid lg:grid-cols-[280px_1fr] gap-6 lg:gap-10 min-w-0">
        {/* Sidebar — screen only */}
        <aside className="space-y-1 no-print min-w-0">
          <div className="text-[11px] font-bold uppercase tracking-widest text-slate-400 mb-2 px-3">
            Platform guide
          </div>
          {sections.map((s) => {
            const Ic = iconFor(s.icon);
            return (
              <button
                key={s.id}
                data-testid={`guide-section-${s.id}`}
                onClick={() => setActive(s.id)}
                className={`w-full text-left flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors truncate ${
                  (current?.id || active) === s.id
                    ? "bg-[#0052FF] text-white"
                    : "text-slate-700 hover:bg-slate-100"
                }`}
              >
                <Ic size={14} className="flex-shrink-0" /> <span className="truncate">{s.title}</span>
              </button>
            );
          })}
          {doc && (
            <div className="mt-6 px-3 text-[11px] text-slate-500 break-words">
              <div>Version <span className="font-mono">{doc.version}</span></div>
              <div>Updated <span className="font-mono">{doc.updated_at}</span></div>
              <div className="text-[10px] text-slate-400 mt-1 break-all">
                Fetched {new Date(doc.generated_at).toLocaleString()}
              </div>
            </div>
          )}
        </aside>

        {/* Main content — screen view */}
        <main className="bg-white rounded-2xl border border-slate-200 p-5 sm:p-8 min-w-0 overflow-hidden no-print-flat" data-testid="guide-content-panel">
          {loading && <div className="text-slate-500 text-sm">Loading the latest guide…</div>}
          {!loading && current && (
            <>
              <div className="text-[11px] font-bold uppercase tracking-widest text-[#0052FF]">
                ProPay Engage360
              </div>
              <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight text-slate-900 mt-2">
                {current.title}
              </h1>
              <GuideBody section={current} />
            </>
          )}
        </main>
      </div>

      {/* Print-only rendering: includes every section for a complete PDF */}
      <div className="print-only">
        <div className="print-cover">
          <AppLogo />
          <div className="mt-10">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#0052FF]">
              ProPay Engage360
            </div>
            <h1 className="font-heading text-5xl font-black mt-3 leading-tight text-slate-900">
              Platform guide<br />and process center
            </h1>
            <p className="text-slate-600 mt-4 max-w-lg">
              Creator procurement, audit control tower and payment platform. Complete reference for
              customer finance and approvers, ProPay operations, auditors, creators and administrators —
              capability map, process, RACI, user levels, screens, integrations and demo playbook.
            </p>
            {doc && (
              <div className="mt-10 text-sm text-slate-500 space-y-1">
                <div>Version {doc.version}</div>
                <div>Updated {doc.updated_at}</div>
                <div>Generated {new Date(doc.generated_at).toLocaleString()}</div>
              </div>
            )}
          </div>
        </div>

        {sections.map((s) => (
          <section key={s.id} className="print-section">
            <div className="text-[11px] font-bold uppercase tracking-widest text-[#0052FF]">
              {s.title}
            </div>
            <h2 className="font-heading text-3xl font-black text-slate-900 mt-2 mb-6">
              {s.title}
            </h2>
            {s.sections.map((b) => (
              <div key={b.h} className="mb-5">
                <div className="font-heading text-lg font-bold text-slate-900">{b.h}{b.tag ? ` — ${b.tag}` : ""}</div>
                <p className="text-slate-700 mt-2 leading-relaxed">{b.p}</p>
              </div>
            ))}
          </section>
        ))}
      </div>
    </div>
  );
}
